import type { Job, JobEvent } from "@webyonetim/contracts";
import { useEffect, useMemo, useState } from "react";

import { Button } from "../../components/ui/Controls";
import {
  AlertBanner,
  ErrorState,
  InlineResult,
  Skeleton,
  StatusBadge,
} from "../../components/ui/Feedback";
import { CodeValue } from "../../components/ui/ResponsiveData";
import { ApiError, API_BASE, jobsApi, releasesApi, type JobLogItem } from "../../lib/api";
import { useJobEvents } from "../../lib/job-events";

export interface JobProgressProps {
  readonly initialJob?: Job;
  readonly jobId: string;
}

const terminalStatuses = new Set<Job["status"]>(["succeeded", "failed", "cancelled"]);

export function JobProgress({ initialJob, jobId }: JobProgressProps) {
  const stream = useJobEvents(jobId);
  const [job, setJob] = useState<Job | undefined>(initialJob);
  const [loading, setLoading] = useState(initialJob === undefined);
  const [loadError, setLoadError] = useState<string | undefined>();
  const [logs, setLogs] = useState<readonly JobLogItem[]>([]);
  const [nextLogCursor, setNextLogCursor] = useState<string | undefined>();
  const [hasMoreLogs, setHasMoreLogs] = useState(false);
  const [logsBusy, setLogsBusy] = useState(false);
  const [logError, setLogError] = useState<string | undefined>();
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelResult, setCancelResult] = useState<Job | undefined>();
  const [cancelError, setCancelError] = useState<string | undefined>();

  useEffect(() => {
    const controller = new AbortController();
    setLoading(initialJob === undefined);
    setLoadError(undefined);
    void jobsApi
      .get(jobId, controller.signal)
      .then((value) => {
        setJob(value);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setLoadError(problemMessage(error));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => {
      controller.abort();
    };
  }, [initialJob, jobId]);

  const streamedJob = useMemo(
    () => stream.events.reduce<Job | undefined>(applyJobEvent, job),
    [job, stream.events],
  );

  const loadLogs = async (cursor?: string) => {
    setLogsBusy(true);
    setLogError(undefined);
    try {
      const page = await releasesApi.logs(jobId, cursor);
      setLogs((current) => mergeLogItems(cursor === undefined ? [] : current, page.items));
      setNextLogCursor(page.page.nextCursor);
      setHasMoreLogs(page.page.hasMore);
    } catch (error: unknown) {
      setLogError(problemMessage(error));
    } finally {
      setLogsBusy(false);
    }
  };

  const cancel = async () => {
    setCancelBusy(true);
    setCancelError(undefined);
    try {
      const value = await jobsApi.cancel(jobId);
      setCancelResult(value);
      setJob(value);
    } catch (error: unknown) {
      setCancelError(problemMessage(error));
    } finally {
      setCancelBusy(false);
    }
  };

  if (loading && streamedJob === undefined) return <Skeleton label="İş durumu yükleniyor" />;
  if (loadError !== undefined && streamedJob === undefined) {
    return <ErrorState requestId={requestIdFromMessage(loadError)} />;
  }
  if (streamedJob === undefined) return null;

  const terminal = terminalStatuses.has(streamedJob.status);
  return (
    <section aria-labelledby={`job-${jobId}-heading`} className="card stack">
      <div className="split-row">
        <div>
          <p className="eyebrow">İş durumu</p>
          <h2 id={`job-${jobId}-heading`}>{jobKindLabel(streamedJob.kind)}</h2>
        </div>
        <StatusBadge status={streamedJob.status} />
      </div>

      <dl className="definition-grid">
        <div>
          <dt>İş kimliği</dt>
          <dd>
            <CodeValue>{streamedJob.id}</CodeValue>
          </dd>
        </div>
        <div>
          <dt>İşlem kimliği</dt>
          <dd>
            <CodeValue>{streamedJob.operationId}</CodeValue>
          </dd>
        </div>
        <div>
          <dt>Aşama</dt>
          <dd>{streamedJob.progress?.message ?? streamedJob.progress?.stage ?? "Hazırlanıyor"}</dd>
        </div>
        <div>
          <dt>Canlı bağlantı</dt>
          <dd>
            {terminal
              ? "Tamamlandı"
              : stream.connected
                ? "Bağlı"
                : stream.reconnecting
                  ? "Yeniden bağlanıyor"
                  : "Bağlantı bekleniyor"}
          </dd>
        </div>
      </dl>

      {streamedJob.progress?.percent === undefined ? null : (
        <div className="stack stack--tight">
          <label htmlFor={`job-${jobId}-progress`}>İlerleme: %{streamedJob.progress.percent}</label>
          <progress id={`job-${jobId}-progress`} max={100} value={streamedJob.progress.percent} />
        </div>
      )}

      {streamedJob.problem === undefined ? null : (
        <AlertBanner heading="İş başarısız oldu" tone="error">
          <p>{streamedJob.problem.detail ?? streamedJob.problem.title}</p>
          <p className="technical">Hata kodu: {streamedJob.problem.code}</p>
          <p>Çalışan mevcut release korunur; trafik doğrulanmadan değiştirilmez.</p>
        </AlertBanner>
      )}

      {cancelResult === undefined ? null : (
        <InlineResult requestId={cancelResult.id} tone="info">
          İptal isteği kaydedildi. Agent işlemi durdurduğunu doğrulayınca durum “İptal edildi” olur.
        </InlineResult>
      )}
      {cancelError === undefined ? null : (
        <AlertBanner heading="İptal isteği gönderilemedi" tone="error">
          <p>{cancelError}</p>
        </AlertBanner>
      )}

      <div className="cluster">
        {streamedJob.cancellable && !terminal ? (
          <Button
            busy={cancelBusy}
            busyLabel="İptal isteniyor…"
            onClick={() => {
              void cancel();
            }}
            variant="destructive"
          >
            İşi iptal et
          </Button>
        ) : null}
        <Button
          busy={logsBusy}
          busyLabel="Loglar yükleniyor…"
          onClick={() => {
            void loadLogs();
          }}
          variant="secondary"
        >
          Logları göster
        </Button>
        <a
          className="button button--quiet"
          href={`${API_BASE}/jobs/${encodeURIComponent(jobId)}/logs/download`}
        >
          Logları indir
        </a>
      </div>

      {logError === undefined ? null : (
        <AlertBanner heading="Loglar yüklenemedi" tone="error">
          <p>{logError}</p>
        </AlertBanner>
      )}
      {logs.length === 0 ? null : (
        <section aria-label="İş logları" className="log-viewer stack stack--tight">
          {logs.map((item) => (
            <article key={`${String(item.sequence)}-${item.digest}`}>
              <header className="split-row">
                <span>{streamLabel(item.stream)}</span>
                <span className="technical">#{item.sequence}</span>
              </header>
              <pre>{item.text}</pre>
            </article>
          ))}
          {hasMoreLogs && nextLogCursor !== undefined ? (
            <Button
              busy={logsBusy}
              onClick={() => {
                void loadLogs(nextLogCursor);
              }}
              variant="secondary"
            >
              Daha fazla log yükle
            </Button>
          ) : null}
        </section>
      )}
    </section>
  );
}

export function applyJobEvent(current: Job | undefined, event: JobEvent): Job | undefined {
  if (event.event === "job.snapshot") return event.data;
  if (current?.id !== event.jobId) return current;
  switch (event.event) {
    case "job.state_changed":
      return { ...current, revision: event.data.revision, status: event.data.status };
    case "job.progress":
      return { ...current, progress: event.data };
    case "job.succeeded":
      return {
        ...current,
        status: "succeeded",
        cancellable: false,
        finishedAt: event.data.finishedAt,
        ...(event.data.result === undefined ? {} : { result: event.data.result }),
      };
    case "job.failed":
      return {
        ...current,
        status: "failed",
        cancellable: false,
        finishedAt: event.data.finishedAt,
        problem: event.data.problem,
      };
    case "job.cancelled":
      return {
        ...current,
        status: "cancelled",
        cancellable: false,
        finishedAt: event.data.finishedAt,
      };
    case "job.cancellation_requested":
      return { ...current, revision: event.data.revision };
    case "job.log_reference":
    case "job.heartbeat":
      return current;
  }
}

function mergeLogItems(
  current: readonly JobLogItem[],
  incoming: readonly JobLogItem[],
): readonly JobLogItem[] {
  const bySequence = new Map(current.map((item) => [item.sequence, item] as const));
  for (const item of incoming) bySequence.set(item.sequence, item);
  return [...bySequence.values()].sort((left, right) => left.sequence - right.sequence);
}

function problemMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return `${error.problem.detail ?? error.problem.title} İstek kimliği: ${error.problem.requestId}`;
  }
  return error instanceof Error
    ? error.message
    : "İşlem tamamlanamadı. Bağlantınızı kontrol edip yeniden deneyin.";
}

function requestIdFromMessage(message: string): string | undefined {
  return /İstek kimliği: (?<requestId>[0-9a-f-]{36})/u.exec(message)?.groups?.["requestId"];
}

function streamLabel(stream: JobLogItem["stream"]): string {
  if (stream === "stdout") return "Uygulama çıktısı";
  if (stream === "stderr") return "Hata çıktısı";
  return "Sistem";
}

function jobKindLabel(kind: string): string {
  const labels: Readonly<Record<string, string>> = {
    "source.probe": "Kaynak bağlantısı kontrolü",
    "source.snapshot": "Kaynak hazırlama",
    "release.build": "Release build",
    "release.promote": "Release yayına alma",
    "release.rollback": "Release geri alma",
    "preview.reconcile": "Önizleme güncelleme",
  };
  return labels[kind] ?? "Arka plan işlemi";
}
